import { api } from '../lib/api';
import type { ChargeType, CustomerOrganisation } from '../types/api';

export async function listCustomers(): Promise<CustomerOrganisation[]> {
  const { data } = await api.get<CustomerOrganisation[]>('/customer-organisations');
  return data;
}

export async function getCustomer(id: string): Promise<CustomerOrganisation> {
  const { data } = await api.get<CustomerOrganisation>(`/customer-organisations/${id}`);
  return data;
}

export interface CreateCustomerInput {
  name: string;
  defaultChargeType?: ChargeType;
}

export async function createCustomer(input: CreateCustomerInput): Promise<CustomerOrganisation> {
  const { data } = await api.post<CustomerOrganisation>('/customer-organisations', input);
  return data;
}

export async function updateOrgStatus(orgId: string, status: 'ACTIVE' | 'INACTIVE'): Promise<CustomerOrganisation> {
  const { data } = await api.patch<CustomerOrganisation>(`/customer-organisations/${orgId}/status`, { status });
  return data;
}

export interface UpdateBrandingInput {
  brandColor?: string;
  invoiceEmail?: string;
}

export async function updateBranding(orgId: string, input: UpdateBrandingInput): Promise<CustomerOrganisation> {
  const { data } = await api.patch<CustomerOrganisation>(`/customer-organisations/${orgId}/branding`, input);
  return data;
}

export async function uploadLogo(orgId: string, file: File): Promise<CustomerOrganisation> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post<CustomerOrganisation>(`/customer-organisations/${orgId}/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// Read-only union of category names in use across the org's sites - used
// for a catalog item scoped "org-wide - every site". Categories themselves
// are managed per-site now, see api/sites.ts's listSiteCategories and co.
export async function listOrgCategoryNames(orgId: string): Promise<string[]> {
  const { data } = await api.get<string[]>(`/customer-organisations/${orgId}/categories`);
  return data;
}

// Plain <img src="..."> can't carry the Authorization header, and the logo
// endpoint is deliberately not @Public() (tenant-scoped like every other
// upload per §7), so the image has to be fetched through axios (with the
// JWT interceptor) as a blob and turned into an object URL - see
// useLogoUrl in hooks/useLogoUrl.ts.
export async function fetchLogoBlob(orgId: string): Promise<Blob> {
  const { data } = await api.get(`/customer-organisations/${orgId}/logo`, { responseType: 'blob' });
  return data;
}
